import {
    booleanAttribute,
    Directive,
    ElementRef,
    forwardRef,
    inject,
    Input,
    numberAttribute,
    OnChanges,
    Renderer2,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator, ValidatorFn } from '@angular/forms';


const isEmptyValue = (value: any): boolean => typeof value === 'undefined' || value === null || value === '' || isNaN(value);

const parseValue = (value: string) => {
    if (value.startsWith('.')) {
        value = `0${value}`;
    }
    if (value.endsWith('.')) {
        value = `${value}0`;
    }
    return parseFloat(value);
};


export type Exclusion = 'none' | 'min' | 'max' | 'both';

export const NumberValidator = (min: number, max: number, exclude: Exclusion, required: boolean): ValidatorFn => control => {
    if (isEmptyValue(control.value) && !required) {
        return null;
    }
    const value = numberAttribute(control.value);
    if (isNaN(value)) {
        return { number: { actual: control.value } };
    }
    const excludeMin = exclude === 'min' || exclude === 'both';
    if (!isEmptyValue(min) && (value < min || (excludeMin && value === min))) {
        return { min: { min, excludeMin, actual: value } };
    }
    const excludeMax = exclude === 'max' || exclude === 'both';
    if (!isEmptyValue(max) && (value > max || (excludeMax && value === max))) {
        return { max: { max, excludeMax, actual: value } };
    }
    return null;
};


@Directive({
    selector: `
        input[type=ui-number][formControl],
        input[type=ui-number][formControlName],
        input[type=ui-number][ngModel]
    `,
    host: {
        '(input)': 'onInput($event)',
        '(change)': 'onChange($event)',
        '(beforeinput)': 'onBeforeInput($event)',
        '(keydown)': 'onKeyDown($event)',
        '(paste)': 'onPaste($event)',
        '(blur)': 'onBlur()',
        '(wheel)': 'onMouseWheel($event)',
        '[attr.inputmode]': '"decimal"',
        '[attr.min]': 'attrMin',
        '[attr.max]': 'attrMax',
        '[attr.step]': 'attrStep',
        '[attr.type]': '"text"',
        '[class.text-right]': 'true',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NumberDirective),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => NumberDirective),
            multi: true,
        },
    ]
})
export class NumberDirective implements OnChanges, ControlValueAccessor, Validator {

    @Input({ transform: numberAttribute }) min: number;

    @Input({ transform: numberAttribute }) max: number;

    @Input({ transform: numberAttribute }) step: number;

    @Input() exclude: Exclusion = 'none';

    @Input({ transform: booleanAttribute }) required: boolean;

    @Input({ transform: booleanAttribute }) intOnly: boolean;

    get attrMin() {
        return isEmptyValue(this.min) ? null : `${this.min}`;
    }

    get attrMax() {
        return isEmptyValue(this.max) ? null : `${this.max}`;
    }

    get attrStep() {
        return isEmptyValue(this.step) ? null : `${this.step}`;
    }

    private touch = () => {};

    private change = (_: string) => {};

    private onValidatorChange = () => {};

    private validator: ValidatorFn;

    private element = inject(ElementRef);

    private renderer = inject(Renderer2);

    ngOnChanges(changes: SimpleChanges) {
        if ('min' in changes || 'max' in changes || 'exclude' in changes) {
            this.createValidator();
            this.onValidatorChange();
        }
    }

    onChange(event: Event) {
        event.preventDefault();
    }

    onBeforeInput(event: InputEvent) {
        if (event.data === null) {
            return;
        }
        event.preventDefault();
        if (/[0-9-]/.test(event.data) || (!this.intOnly && (!/[.]/.test(this.element.nativeElement.value) && /[.]/.test(event.data)))) {
            this.insertText(event.data);
        }
    }

    onInput(event: InputEvent) {
        // capture backspace and delete if matches pattern
        if (!/^-?[0-9]*[.]?[0-9]*$/.test(this.element.nativeElement.value)) {
            event.preventDefault();
            return;
        }
        this.change(this.element.nativeElement.value);
    }

    onBlur() {
        this.touch();
    }

    onPaste(event: ClipboardEvent) {
        event.preventDefault();
        if (event.clipboardData) {
            const text = event.clipboardData.getData('text');
            let out = '';
            let pattern = /[0-9.-]/;

            if (this.intOnly) {
                pattern = /[0-9-]/;
            }

            for (const char of text) {
                if (pattern.test(char)) {
                    if (char === '.') {
                        pattern = /[0-9-]/;
                    }
                    out = out + char;
                }
            }

            this.insertText(out);
        }
    }

    onKeyDown(event: KeyboardEvent) {
        const change = event.key === 'ArrowUp' ? 1 : (event.key === 'ArrowDown' ? -1 : 0);
        if (!change) {
            return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.incValue(change);
    }

    onMouseWheel(event: WheelEvent) {
        // ignore if not focused
        if (event.target !== document.activeElement) {
            return;
        }
        const change = event.deltaY < 0 ? 1 : (event.deltaY > 0 ? -1 : 0);
        if (!change) {
            return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.incValue(change);
    }

    private incValue(step: number) {
        const el = this.element.nativeElement;
        const value = (numberAttribute(el.value) || 0) + step;
        if ((isEmptyValue(this.min) || value >= this.min) && (isEmptyValue(this.max) || value <= this.max)) {
            this.writeValue(value);
            this.change(el.value);
        }
    }

    private insertText(text: string) {
        const el = this.element.nativeElement;
        const start = el.selectionStart;
        const end = el.selectionEnd;

        if (start !== 0 || /-/.test(el.value)) {
            text = text.replace('-', '');
        }

        if (el.value.includes('.') && !el.value.substring(start, end).includes('.') && text.includes('.')) {
            text = text.replace('.', '');
        }

        const value = `${el.value.substring(0, start)}${text}${el.value.substring(end)}`;
        this.renderer.setProperty(el, 'value', value);
        // restore cursor
        el.setSelectionRange(start + text.length, start + text.length);
        this.change(el.value);
    }

    registerOnChange(fn: (value: number | null) => void) {
        this.change = value => fn(isEmptyValue(value) ? null : parseValue(value));
    }

    registerOnTouched(fn: () => void) {
        this.touch = fn;
    }

    writeValue(value: unknown) {
        this.renderer.setProperty(this.element.nativeElement, 'value', isEmptyValue(value) ? null : value);
    }

    setDisabledState(isDisabled: boolean) {
        this.renderer.setProperty(this.element.nativeElement, 'disabled', isDisabled);
    }

    private createValidator() {
        const min = isNaN(this.min) ? Number.MIN_SAFE_INTEGER : this.min;
        const max = isNaN(this.max) ? Number.MAX_SAFE_INTEGER : this.max;
        this.validator = NumberValidator(min, max, this.exclude, this.required);
    }

    validate(control: AbstractControl) {
        return this.validator ? this.validator(control) : null;
    }

    registerOnValidatorChange(fn: () => void) {
        this.onValidatorChange = fn;
    }
}
